import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type PointerEvent as ReactPointerEvent,
} from 'react';

/**
 * Hand-rolled pinch-to-zoom + pan + double-tap toggle, built on pointer
 * events (no extra dependency).
 *
 * Pragmatic scope — this ships the 80%:
 *   - one and two-finger gestures only
 *   - no rotation
 *   - no inertial fling (sufficient for static pages)
 *   - no momentum scrolling
 *   - state clamped to a sane scale range (1 to 5)
 *
 * The returned `style` is meant to be spread onto the element you want to
 * transform; the wrapper handlers go on the same element. The actual touch-
 * action and overflow management is the caller's job (see HorizontalSwipe).
 */

interface Point {
  x: number;
  y: number;
}

interface ZoomState {
  scale: number;
  tx: number;
  ty: number;
}

const IDENTITY: ZoomState = { scale: 1, tx: 0, ty: 0 };
const MIN_SCALE = 1;
const MAX_SCALE = 5;
const DOUBLE_TAP_MS = 280;
const DOUBLE_TAP_PX = 24;

export interface UsePinchZoomResult {
  /** Spread onto the element you want to pan/zoom. */
  handlers: {
    onPointerDown: (e: ReactPointerEvent<HTMLElement>) => void;
    onPointerMove: (e: ReactPointerEvent<HTMLElement>) => void;
    onPointerUp: (e: ReactPointerEvent<HTMLElement>) => void;
    onPointerCancel: (e: ReactPointerEvent<HTMLElement>) => void;
  };
  /** Apply via `style={style}` on the transformed element. */
  style: CSSProperties;
  /** Whether the user has zoomed beyond the natural fit. */
  zoomed: boolean;
  /** Reset to identity (e.g. when changing pages). */
  reset: () => void;
}

export function usePinchZoom(): UsePinchZoomResult {
  const [state, setState] = useState<ZoomState>(IDENTITY);
  const stateRef = useRef<ZoomState>(IDENTITY);
  stateRef.current = state;

  const pointersRef = useRef<Map<number, Point>>(new Map());
  /** Distance + midpoint when the second pointer went down. */
  const pinchStartRef = useRef<{
    dist: number;
    mid: Point;
    scale: number;
    tx: number;
    ty: number;
  } | null>(null);
  const dragStartRef = useRef<{
    pt: Point;
    tx: number;
    ty: number;
  } | null>(null);
  /** Last single-tap timestamp + position, for double-tap detection. */
  const lastTapRef = useRef<{ t: number; x: number; y: number } | null>(null);

  const reset = useCallback(() => {
    setState(IDENTITY);
    pointersRef.current.clear();
    pinchStartRef.current = null;
    dragStartRef.current = null;
  }, []);

  // Reset on unmount to avoid stale pointer captures across remounts.
  useEffect(() => () => reset(), [reset]);

  const clamp = (s: ZoomState): ZoomState => {
    const scale = Math.max(MIN_SCALE, Math.min(MAX_SCALE, s.scale));
    return { scale, tx: s.tx, ty: s.ty };
  };

  const onPointerDown = useCallback((e: ReactPointerEvent<HTMLElement>) => {
    // Only react to primary mouse / touch / pen.
    if (e.button !== 0 && e.pointerType === 'mouse') return;
    (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
    const pt = { x: e.clientX, y: e.clientY };
    pointersRef.current.set(e.pointerId, pt);

    if (pointersRef.current.size === 1) {
      // Possible double-tap.
      const now = performance.now();
      const last = lastTapRef.current;
      if (last && now - last.t < DOUBLE_TAP_MS) {
        const dx = pt.x - last.x;
        const dy = pt.y - last.y;
        if (Math.hypot(dx, dy) < DOUBLE_TAP_PX) {
          setState((cur) => {
            if (cur.scale > 1.001) return IDENTITY;
            // Zoom in to ~2x centered roughly on the tap.
            const targetScale = 2;
            const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
            const cx = pt.x - rect.left - rect.width / 2;
            const cy = pt.y - rect.top - rect.height / 2;
            return clamp({
              scale: targetScale,
              tx: -cx * (targetScale - 1),
              ty: -cy * (targetScale - 1),
            });
          });
          lastTapRef.current = null;
          return;
        }
      }
      lastTapRef.current = { t: now, x: pt.x, y: pt.y };

      // Start a single-pointer drag (only meaningful while zoomed).
      dragStartRef.current = {
        pt,
        tx: stateRef.current.tx,
        ty: stateRef.current.ty,
      };
    } else if (pointersRef.current.size === 2) {
      // Start a pinch.
      const pts = [...pointersRef.current.values()];
      const a = pts[0]!;
      const b = pts[1]!;
      const dist = Math.hypot(b.x - a.x, b.y - a.y);
      pinchStartRef.current = {
        dist,
        mid: { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 },
        scale: stateRef.current.scale,
        tx: stateRef.current.tx,
        ty: stateRef.current.ty,
      };
      dragStartRef.current = null;
    }
  }, []);

  const onPointerMove = useCallback((e: ReactPointerEvent<HTMLElement>) => {
    const tracked = pointersRef.current.get(e.pointerId);
    if (!tracked) return;
    pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });

    if (pointersRef.current.size >= 2 && pinchStartRef.current) {
      const pts = [...pointersRef.current.values()];
      const a = pts[0]!;
      const b = pts[1]!;
      const dist = Math.hypot(b.x - a.x, b.y - a.y);
      const start = pinchStartRef.current;
      const scale = (dist / start.dist) * start.scale;
      // Translate so the pinch midpoint stays approximately put.
      const newMid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
      const dx = newMid.x - start.mid.x;
      const dy = newMid.y - start.mid.y;
      setState(
        clamp({
          scale,
          tx: start.tx + dx,
          ty: start.ty + dy,
        }),
      );
      return;
    }

    if (pointersRef.current.size === 1 && dragStartRef.current) {
      // Only pan when zoomed in; otherwise let the parent handle swipes.
      if (stateRef.current.scale <= 1.001) return;
      const drag = dragStartRef.current;
      const dx = e.clientX - drag.pt.x;
      const dy = e.clientY - drag.pt.y;
      setState({
        scale: stateRef.current.scale,
        tx: drag.tx + dx,
        ty: drag.ty + dy,
      });
    }
  }, []);

  const onPointerUp = useCallback((e: ReactPointerEvent<HTMLElement>) => {
    pointersRef.current.delete(e.pointerId);
    if (pointersRef.current.size < 2) pinchStartRef.current = null;
    if (pointersRef.current.size === 0) dragStartRef.current = null;
  }, []);

  const onPointerCancel = useCallback((e: ReactPointerEvent<HTMLElement>) => {
    pointersRef.current.delete(e.pointerId);
    pinchStartRef.current = null;
    dragStartRef.current = null;
  }, []);

  return {
    handlers: { onPointerDown, onPointerMove, onPointerUp, onPointerCancel },
    style: {
      transform: `translate3d(${state.tx}px, ${state.ty}px, 0) scale(${state.scale})`,
      transformOrigin: 'center center',
      touchAction: state.scale > 1.001 ? 'none' : 'pan-y',
      willChange: 'transform',
    },
    zoomed: state.scale > 1.001,
    reset,
  };
}
